// ===== 新規 / 編集 共用フォーム =====

import { useEffect, useMemo, useState } from "react";
import { Alert, Button, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { PageContext } from "../PageContext";
import Time from "../../../util/Time";
import { ContentLoading } from "../../../components/Loading";
import { GetCookieValue, SetCookie } from "../../../util/CookieManager";
import { useSelectedWork, resolveWorkId } from "../../../util/useSelectedWork";
import { WorkBase } from "../../../schema/generated/Work";
import { CarryInSelectingWorkCacheKey, LocationRow, workLabel } from "./helpers";
import { required } from "../../../util/typed";

export function CarryInFormPage({ pageContext, workList, locationList, mode }: { pageContext: PageContext; workList: WorkBase[]; locationList: LocationRow[]; mode: "new" | "edit" }) {
  const api = pageContext.api;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [workId, setWorkId] = useState<number | "">("");
  const [persistedWorkId] = useSelectedWork();
  const [cilId, setCilId] = useState<number | "">("");
  const [temperature, setTemperature] = useState<number | "">("");
  const [humidity, setHumidity] = useState<number | "">("");
  const [receiver, setReceiver] = useState("");
  const [receivedAt, setReceivedAt] = useState("");
  const [requireCount, setRequireCount] = useState<number>(0);
  const [remarks, setRemarks] = useState("");
  const [loaded, setLoaded] = useState(mode === "new");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // 新規は cookie の工事(無ければ計画業務ページと共通の選択中工事、無ければ先頭工事)を初期選択。
  useEffect(() => {
    if (mode === "new" && workId === "" && workList.length > 0) {
      const cached = GetCookieValue(CarryInSelectingWorkCacheKey);
      const cachedId = cached != null && cached !== "" ? Number(cached) : NaN;
      if (Number.isFinite(cachedId) && workList.some((w) => w.id === cachedId)) {
        setWorkId(cachedId);
      } else {
        setWorkId(resolveWorkId(persistedWorkId, workList));
      }
    }
  }, [mode, workList]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode === "new" || !id) { return; }
    let active = true;
    api.carry_in.getFromId({ id }).then((ci) => {
      if (!active) { return; }
      setWorkId(ci.work_id ?? "");
      setCilId(ci.carry_in_location_id ?? "");
      setTemperature(ci.temperature ?? "");
      setHumidity(ci.humidity ?? "");
      setReceiver(ci.receiver_name ?? "");
      setReceivedAt(ci.received_at != null ? new Time(ci.received_at).Format("YYYY/MM/dd HH:mm:ss") : "");
      setRequireCount(ci.require_material_count);
      setRemarks(ci.remarks ?? "");
      setLoaded(true);
    }).catch(() => { if (active) { setLoaded(true); } });
    return () => { active = false; };
  }, [api, mode, id]);

  // 選択中の工事(Work)の area_division_id で搬入場所を絞込む(mock CarryInPage.tsx:407-414 準拠)。
  const selectedWork = useMemo(() => workList.find((w) => w.id === workId), [workList, workId]);
  const areaDivId = selectedWork?.area_division_id;
  const carryInLocationOptions = useMemo(() => {
    return locationList.filter((l) => areaDivId == null || l.area_division_id === areaDivId);
  }, [locationList, areaDivId]);

  const onWorkChange = (next: number | "") => {
    setWorkId(next);
    setCilId("");
  };

  const submit = async () => {
    if (workId === "") { setError("工事を選択してください"); return; }
    if (!requireCount || requireCount < 1) { setError("予定数 (1 以上) を入力してください"); return; }
    const receivedEpoch = receivedAt.trim() === "" ? null : new Date(receivedAt).getTime();
    if (receivedEpoch != null && Number.isNaN(receivedEpoch)) { setError("搬入日時の形式が不正です (例: 2026/07/15 10:30:00)"); return; }
    setSubmitting(true);
    try {
      if (mode === "new") {
        const res = await api.carry_in.put({
          work_id: workId,
          require_material_count: requireCount,
          carry_in_location_id: cilId === "" ? undefined : cilId,
          received_at: receivedEpoch ?? undefined,
          temperature: temperature === "" ? undefined : temperature,
          humidity: humidity === "" ? undefined : humidity,
          receiver_name: receiver.trim() || undefined,
          planned_count: requireCount,
          remarks: remarks.trim() || undefined,
        });
        SetCookie(CarryInSelectingWorkCacheKey, String(workId), { maxAge: 365 * Time.ADay });
        navigate(`/console/carry-in/${res.id}/`);
      } else {
        await api.carry_in.patch({
          id: required(id, "URLの搬入ID"),
          work_id: workId,
          require_material_count: requireCount,
          carry_in_location_id: cilId === "" ? null : cilId,
          received_at: receivedEpoch,
          temperature: temperature === "" ? null : temperature,
          humidity: humidity === "" ? null : humidity,
          receiver_name: receiver.trim() || null,
          planned_count: requireCount,
          remarks: remarks.trim() || null,
        });
        navigate(`/console/carry-in/${id}/`);
      }
    } catch {
      setError("保存に失敗しました。もう一度お試しください");
      setSubmitting(false);
    }
  };

  if (!loaded) {
    return <ContentLoading />;
  }

  return (
    <Stack p={3} gap={2} maxWidth={900} mx="auto">
      <Typography variant="h4" fontWeight="bold">搬入情報{mode === "edit" ? "の編集" : ""}</Typography>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack gap={2} maxWidth={600} mx="auto">
          <TextField select label="工事" value={workList.some((w) => w.id === workId) ? workId : ""} onChange={(e) => onWorkChange(e.target.value === "" ? "" : Number(e.target.value))} fullWidth>
            <MenuItem value="" disabled><em>工事を選択</em></MenuItem>
            {workList.map((w) => <MenuItem key={w.id} value={w.id}>{workLabel(w)}</MenuItem>)}
          </TextField>
          <Alert severity="info" sx={{ py: 0.5 }}>搬入する材料ロットは作成時に選びません。搬入後の QR スキャンで紐付けます（複数ロットが混在してOK）。</Alert>
          <TextField select label="搬入場所 (任意)" value={cilId} onChange={(e) => setCilId(e.target.value === "" ? "" : Number(e.target.value))}
            helperText={workId !== "" && carryInLocationOptions.length === 0 ? "当該工事のエリアに紐付く搬入場所が未登録です" : ""} fullWidth>
            <MenuItem value="">未選択</MenuItem>
            {carryInLocationOptions.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
          </TextField>
          <Stack direction="row" gap={2}>
            <TextField type="number" label="気温(℃) (任意)" inputProps={{ step: 0.1 }} value={temperature} onChange={(e) => setTemperature(e.target.value === "" ? "" : Number(e.target.value))} sx={{ flex: 1 }} helperText="未測定可。記録すると温湿度推移表に反映" />
            <TextField type="number" label="湿度(%) (任意)" inputProps={{ step: 1, min: 0, max: 100 }} value={humidity} onChange={(e) => setHumidity(e.target.value === "" ? "" : Number(e.target.value))} sx={{ flex: 1 }} />
          </Stack>
          <TextField label="搬入者" value={receiver} onChange={(e) => setReceiver(e.target.value)} fullWidth />
          <TextField label="搬入日時 (任意)" placeholder="2026/07/15 10:30:00" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} fullWidth />
          <TextField type="number" label="予定数 (QR枚数)" inputProps={{ min: 1, step: 1 }} value={requireCount || ""} onChange={(e) => setRequireCount(Math.max(0, Math.floor(Number(e.target.value) || 0)))} fullWidth />
          <TextField label="備考" multiline minRows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth />
          {error && <Alert severity="error">{error}</Alert>}
          <Stack alignItems="center">
            <Button variant="contained" size="large" disabled={submitting} onClick={submit} sx={{ py: 1.5, width: "60%" }}>{mode === "new" ? "追加する" : "更新"}</Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
